<template>

        <p-modal name="exportInventoryValueModal"
                 label="Export Total Inventory Value"
                 ref="exportInventoryValueModal"
                 size="lg"
                 @show="loadParameters">
            <template v-slot:default>
                <div class="container-fluid content-wrapper">
                    <b-row cols="1" cols-md="2">
                        <b-col>
                            <p-card class="mb-2 px-2 py-2" no-body>
                                <p-filter-select item-text="text"
                                                 data-type="productTypes"
                                                 label="Product Type"
                                                 v-model="reportParameters.productTypes"
                                                 :default-selected="true"
                                                 :clear-button="false" />
                            </p-card>
                            <p-select v-model="reportParameters.negativeInventory"
                                      name="negativeInventory"
                                      label="Negative Inventory"
                                      :options="negativeInventoryOptions" />
                            <p-checkbox size="md"
                                        class="mt-3"
                                        label="Include inventory detail"
                                        v-model="
                                    reportParameters.includeInventoryDetail
                                " />
                            <p-checkbox size="md"
                                        label="Only include inventory where SOH is not zero"
                                        v-model="reportParameters.onlyStockNotZero" />
                        </b-col>
                        <b-col>
                            <p-card class="mb-2 ps-2 py-2" no-body>
                                <label class="text-sm"
                                       v-if="dccSelectorHelpText">{{ dccSelectorHelpText }}</label>
                                <DCCsSelector ref="dccSelector" class="container-fluid"
                                              @selection-change="
                                        reportParameters.dccIds = $event
                                    " />
                            </p-card>
                        </b-col>
                    </b-row>
                    <UserReportTemplate 
                        :user-report-template-options="userReportTemplateOptions"
                        v-model:selected-user-report-template-id="selectedUserReportTemplateId"
                        :selected-user-report-template="selectedUserReportTemplate"
                        v-model:template-name="templateName"
                        @selected-object="applyUserReportTemplate($event)" />
                </div>
            </template>
            <template v-slot:modal-footer>
                <b-row>
                    <b-col>
                        <b-overlay :show="isBusy"
                                   rounded
                                   opacity="0.5"
                                   spinner-small
                                   spinner-variant="primary"
                                   class="d-inline-block">
                            <b-dropdown text="Export"
                                        variant="outline-primary">
                                <b-dropdown-item @click="exportInventoryValueReport('pdf')">PDF</b-dropdown-item>
                                <b-dropdown-item @click="exportInventoryValueReport('csv')">CSV</b-dropdown-item>
                            </b-dropdown>
                        </b-overlay>
                    </b-col>
                </b-row>
            </template>
        </p-modal>
</template>

<script>

import reports from './reports.js';
import selectListOptionsDataContext from '@/services/selectListOptions.dataContext.js';
import ReportTemplates from '@/components/mixins/ReportTemplates.js';
import UserReportTemplate from '@/components/UserReportTemplate.vue';

export default {
    props: {},
    mixins: [reports, ReportTemplates],
    data() {
        return {
            // Identifies which report saved templates belong to; must match the report name in Reports.vue.
            reportName: 'Total Inventory Value',
        };
    },
     components: {
        UserReportTemplate
    },
    methods: {
        async exportInventoryValueReport(type) {
            if (!(await this.saveUserReportTemplate())) {
                return;
            }

            let destination = type === 'pdf' ? '/physicalInventory/exportInventoryValue' : '/physicalInventory/exportInventoryValueCsv';
            let message = `Total Inventory Value report generated successfully.`;
            let fileName = 'Total Inventory Value';
            this.exportReport(type, destination, message, fileName);
        },

        async show() {
            // loadParameters resets dccIds on @show, so clear the tree too or its leftover
            // checkmarks would disagree with the empty selection. Matches the sibling DCC modals.
            this.$refs.dccSelector?.clearSelections();
            this.$refs.exportInventoryValueModal.show();
            await this.loadUserReportTemplates();
        },

        // ReportTemplates hook: return false when anything in the saved template can no longer be
        // selected, and the mixin warns and loads nothing. Product types and negative inventory are
        // fixed lists but checked the same way for consistency; DCCs can be removed, so they're
        // checked against the selector's own tree. The two checkbox flags are literal values.
        async validateReportParameters(savedParameters) {
            const productTypes = await selectListOptionsDataContext.getStoreDropdownData('productTypes');

            const hasValidProductTypes = (savedParameters.productTypes ?? []).every(productTypeId =>
                productTypes.some(productType => productType.value == productTypeId));

            if (!hasValidProductTypes) {
                return false;
            }

            if (!this.negativeInventoryOptions.some(negativeInventoryOption => negativeInventoryOption.value === savedParameters.negativeInventory)) {
                return false;
            }

            return this.$refs.dccSelector.hasAllDccIds(savedParameters.dccIds);
        },

        // ReportTemplates hook: DCCsSelector owns its own tree selection, so the restored ids have
        // to be pushed back into it. Called unconditionally — a saved template with no DCCs has to
        // clear whatever is selected now, not be skipped.
        async onUserReportTemplateApplied() {
            await this.$refs.dccSelector.restoreSelections(this.reportParameters.dccIds);
        }
    }
};

</script>

<style scoped></style>
